/**
 * Problema e Kaizen são entidades diferentes.
 * O Kaizen só nasce quando alguém confirma o formulário.
 */
import type { Issue } from '../types/issue';
import type { KaizenStatus } from '../types/gainsMonitor';
import { readOperationalOrigin } from '../components/ops/exceptionLoop';

export const KAIZEN_STATUS_LABELS: Record<KaizenStatus, string> = {
  planejado: 'Planejado',
  em_implementacao: 'Em implementação',
  implementado: 'Implementado',
  em_monitoramento: 'Em monitoramento',
  sustentado: 'Sustentado',
  regredido: 'Regredido',
};

export interface KaizenOriginDraft {
  /** Contexto visual do formulário. Não é coluna de kaizen_records. */
  problem_id: string;
  problem_label: string;
  problem_title: string;
  title: string;
  description: string;
  project_id: string | null;
  workflow_id: string | null;
  responsible: '';
  status: 'planejado';
  operational_label: string | null;
  return_path: string | null;
  opening_wip: number | null;
}

export interface KaizenMetricSource {
  baseline_lead_time?: number | null;
  target_lead_time?: number | null;
  current_lead_time?: number | null;
  baseline_cycle_time?: number | null;
  target_cycle_time?: number | null;
  current_cycle_time?: number | null;
  baseline_ftr?: number | null;
  target_ftr?: number | null;
  current_ftr?: number | null;
  baseline_capacity_fte?: number | null;
  target_capacity_fte?: number | null;
  current_capacity_fte?: number | null;
  baseline_cost?: number | null;
  target_cost?: number | null;
  current_cost?: number | null;
}

export interface OperationalMetricLine {
  key: string;
  label: string;
  unit: string;
  before: number | null;
  target: number | null;
  current: number | null;
}

const METRICS: Array<{
  key: string;
  label: string;
  unit: string;
  baseline: keyof KaizenMetricSource;
  target: keyof KaizenMetricSource;
  current: keyof KaizenMetricSource;
}> = [
  { key: 'lead_time', label: 'Lead time', unit: 'min', baseline: 'baseline_lead_time', target: 'target_lead_time', current: 'current_lead_time' },
  { key: 'cycle_time', label: 'Cycle time', unit: 'min', baseline: 'baseline_cycle_time', target: 'target_cycle_time', current: 'current_cycle_time' },
  { key: 'ftr', label: 'FTR', unit: '%', baseline: 'baseline_ftr', target: 'target_ftr', current: 'current_ftr' },
  { key: 'capacity_fte', label: 'Capacidade', unit: 'FTE', baseline: 'baseline_capacity_fte', target: 'target_capacity_fte', current: 'current_capacity_fte' },
  { key: 'cost', label: 'Custo', unit: 'R$', baseline: 'baseline_cost', target: 'target_cost', current: 'current_cost' },
];

export function canOfferTreatAsKaizen(status: string): boolean {
  return status !== 'closed';
}

/** Quem já pode gravar na aplicação. Visualizador não cria por este caminho. */
export function canCreateKaizen(canEdit: boolean): boolean {
  return canEdit;
}

export function showEscalationPrompt(input: {
  status: string;
  hasIneffectiveCheck: boolean;
  canClose: boolean;
}): boolean {
  return input.status !== 'closed' && input.hasIneffectiveCheck && !input.canClose;
}

export function kaizenCtaLabel(input: { hasIneffectiveCheck: boolean; existingCount: number }): string {
  if (input.existingCount > 0) return 'Criar outro Kaizen';
  if (input.hasIneffectiveCheck) return 'Escalar para Kaizen';
  return 'Tratar como Kaizen';
}

/** O clique abre o formulário. Nada é gravado antes da confirmação. */
export function ctaCreatesKaizenImmediately(): false {
  return false;
}

export function problemRefLabel(issue: { id: string; code?: string | null }): string {
  const code = issue.code?.trim();
  return code ? `Problema ${code}` : `Problema ${issue.id.slice(0, 8)}`;
}

export function kaizenRefLabel(kaizen: { id: string }): string {
  return `Kaizen ${kaizen.id.slice(0, 8)}`;
}

/**
 * Pré-preenche só o que o problema já guarda com a mesma semântica.
 * WIP, lead time e capacidade não são copiados para baseline.
 * Responsável do problema é usuário; responsável do Kaizen é texto. Não há cópia.
 */
export function buildKaizenDraftFromIssue(issue: Issue): KaizenOriginDraft {
  const origin = readOperationalOrigin(issue.metadata);
  return {
    problem_id: issue.id,
    problem_label: problemRefLabel(issue),
    problem_title: issue.title,
    title: issue.title,
    description: issue.description?.trim() ?? '',
    project_id: issue.project_id ?? null,
    workflow_id: issue.workflow_id ?? null,
    responsible: '',
    status: 'planejado',
    operational_label: origin?.label ?? null,
    return_path: origin?.return_path ?? null,
    opening_wip: typeof origin?.wip === 'number' ? origin.wip : null,
  };
}

export function issueStatusAfterKaizenCreate<T extends string>(status: T): T {
  return status;
}

export function kaizenStatusClosesProblem(_status: KaizenStatus): false {
  return false;
}

function finiteMetric(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function operationalMetricLines(record: KaizenMetricSource): OperationalMetricLine[] {
  const lines: OperationalMetricLine[] = [];
  for (const metric of METRICS) {
    const before = finiteMetric(record[metric.baseline]);
    const target = finiteMetric(record[metric.target]);
    const current = finiteMetric(record[metric.current]);
    if (before == null && target == null && current == null) continue;
    lines.push({
      key: metric.key,
      label: metric.label,
      unit: metric.unit,
      before,
      target,
      current,
    });
  }
  return lines;
}

export function formatMetricValue(value: number, unit: string): string {
  if (unit === 'R$') return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  if (unit === '%') return `${value}%`;
  if (unit === 'FTE') return `${value} FTE`;
  return `${value} ${unit}`;
}

export function formatMetricSpan(line: OperationalMetricLine): string {
  const parts: string[] = [];
  if (line.before != null) parts.push(formatMetricValue(line.before, line.unit));
  if (line.target != null) parts.push(`meta ${formatMetricValue(line.target, line.unit)}`);
  if (line.current != null) parts.push(`atual ${formatMetricValue(line.current, line.unit)}`);
  return parts.join(' → ');
}
